/**
 * Premium Avatar Component with Skeleton Loader
 *
 * Features:
 * - Smooth shimmer skeleton animation while loading
 * - Graceful fade-in transition when image loads
 * - Fallback to initials or default avatar on error
 * - Multiple size variants
 *
 * Inspired by Linear, Stripe, and Airbnb design patterns.
 */

import React, { useState, useEffect, useCallback } from 'react';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarWithSkeletonProps {
  src?: string | null;
  alt: string;
  fallbackSrc?: string;
  fallbackInitials?: string;
  size?: AvatarSize;
  className?: string;
  showVerifiedBadge?: boolean;
}

const sizeConfig: Record<AvatarSize, { dimension: string; text: string; badge: string }> = {
  xs: { dimension: 'w-6 h-6', text: 'text-[10px]', badge: 'w-3 h-3 -right-0.5 -bottom-0.5' },
  sm: { dimension: 'w-8 h-8', text: 'text-xs', badge: 'w-3.5 h-3.5 -right-0.5 -bottom-0.5' },
  md: { dimension: 'w-10 h-10', text: 'text-sm', badge: 'w-4 h-4 -right-0.5 -bottom-0.5' },
  lg: { dimension: 'w-12 h-12', text: 'text-base', badge: 'w-4.5 h-4.5 -right-1 -bottom-1' },
  xl: { dimension: 'w-16 h-16', text: 'text-lg', badge: 'w-5 h-5 -right-1 -bottom-1' },
};

export default function AvatarWithSkeleton({
  src,
  alt,
  fallbackSrc = '/img/default_avatar.jpeg',
  fallbackInitials,
  size = 'md',
  className = '',
  showVerifiedBadge = false,
}: AvatarWithSkeletonProps) {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [currentSrc, setCurrentSrc] = useState(src);

  const config = sizeConfig[size];

  // Reset state when src changes
  useEffect(() => {
    if (!src) {
      setImageState('error');
      return;
    }

    setImageState('loading');
    setCurrentSrc(src);

    // Preload image to check if it's already cached
    const img = new Image();
    img.src = src;

    if (img.complete && img.naturalWidth > 0) {
      setImageState('loaded');
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setImageState('loaded');
  }, []);

  const handleError = useCallback(() => {
    if (currentSrc !== fallbackSrc && fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setImageState('loading');
    } else {
      setImageState('error');
    }
  }, [currentSrc, fallbackSrc]);

  // Generate initials from alt text
  const getInitials = (): string => {
    if (fallbackInitials) return fallbackInitials;
    return alt
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={`relative ${config.dimension} ${className}`}>
      {/* Skeleton loader with shimmer effect */}
      <div
        className={`
          absolute inset-0 rounded-full overflow-hidden
          transition-opacity duration-300 ease-out
          ${imageState === 'loaded' ? 'opacity-0' : 'opacity-100'}
        `}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700">
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-gray-500/30"
            style={{
              animation: 'shimmer 1.5s infinite',
              transform: 'translateX(-100%)',
            }}
          />
        </div>
      </div>

      {/* Image */}
      {imageState !== 'error' && currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`
            absolute inset-0 w-full h-full object-cover rounded-full
            transition-opacity duration-300 ease-out
            ${imageState === 'loaded' ? 'opacity-100' : 'opacity-0'}
          `}
        />
      )}

      {/* Fallback initials */}
      {imageState === 'error' && (
        <div
          className={`
            absolute inset-0 rounded-full flex items-center justify-center
            bg-gradient-to-br from-[#7549F4] to-[#FCA6E9]
            font-medium text-white ${config.text}
          `}
        >
          {getInitials()}
        </div>
      )}

      {/* Verified badge */}
      {showVerifiedBadge && (
        <div className={`absolute ${config.badge} bg-white rounded-full p-0.5`}>
          <svg
            viewBox="0 0 24 24"
            className="w-full h-full text-[#00a0ff]"
            fill="currentColor"
          >
            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" />
          </svg>
        </div>
      )}

      {/* Inline keyframes for shimmer animation */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Skeleton-only component for loading states
 */
export function AvatarSkeleton({
  size = 'md',
  className = ''
}: {
  size?: AvatarSize;
  className?: string;
}) {
  const config = sizeConfig[size];

  return (
    <div className={`relative ${config.dimension} ${className}`}>
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200">
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
            style={{
              animation: 'shimmer 1.5s infinite',
              transform: 'translateX(-100%)',
            }}
          />
        </div>
      </div>
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
}
